import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { PersonReportCard } from '@/components/common/PersonReportCard';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { actKindLabel, dispositionLabel, officeKindLabel } from '@/pages/ibama/judgeLabels';
import { fetchIbamaConamaRelator, fetchIbamaJudge } from '@/services/ibama';
import type { AnswerOut, IbamaJudgeCount, IbamaJudgeProfile, PersonReportOut } from '@/services/types';

/**
 * One named judging individual on IBAMA's records.
 *
 * The office track shows the term windows the published acts define and the
 * judgments dated inside them - attribution by incumbency, disclosed per row,
 * never presented as a signature. The CONAMA track shows the items the CER
 * minutes minute this person as relator on, with the disposition counts
 * refused below the floor.
 */
/** '02020000645200552' -> '02020.000645/2005-52'; anything else passes through. */
function formatNup(digits: string): string {
  return /^\d{17}$/.test(digits)
    ? `${digits.slice(0, 5)}.${digits.slice(5, 11)}/${digits.slice(11, 15)}-${digits.slice(15)}`
    : digits;
}

export function IbamaJudgeProfilePage({ conama = false }: { conama?: boolean }) {
  const { slug = '' } = useParams();
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const number = new Intl.NumberFormat(i18n.language);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ibama', 'judge', conama ? 'conama' : 'any', slug],
    queryFn: () => (conama ? fetchIbamaConamaRelator(slug) : fetchIbamaJudge(slug)),
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <Answer answer={data.profile}>
        {(p) => <Profile p={p} data={data} t={t} number={number} />}
      </Answer>
    </div>
  );
}

function Profile({
  p,
  data,
  t,
  number,
}: {
  p: IbamaJudgeProfile;
  data: {
    as_of: Parameters<typeof AsOfStrip>[0]['asOf'];
    disposition_mix: AnswerOut<IbamaJudgeCount[]>;
    attributed_cases: {
      auto_key: string;
      uf: string;
      instance: string;
      attribution_basis: string;
      decision_date: string | null;
      proof_act_url: string | null;
    }[];
    conama_items: {
      process_number: string;
      reunion: number;
      when: string | null;
      disposition: string | null;
      resultado: string;
      process_url: string | null;
      basis: string;
    }[];
    reports?: Record<string, PersonReportOut>;
  };
  t: (key: string, opts?: Record<string, unknown>) => string;
  number: Intl.NumberFormat;
}) {
  // The label helpers take i18next's own TFunction; the `t` prop is typed
  // structurally for the rest of this component.
  const { t: tf } = useTranslation(['ibama']);
  return (
    <>
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground" data-person-name>
          {p.name}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(`ibama:judges.track.${p.track}`)} · {officeKindLabel(tf, p.kind)}
          {p.uf ? ` (${p.uf})` : ''}
          {p.first_year || p.last_year
            ? ` · ${p.first_year ?? '—'}–${p.last_year ?? '—'}`
            : ''}
        </p>
      </header>

      <PersonReportCard reports={data.reports} />

      <ExplainerCard
        i18nKey={
          p.track === 'conama' ? 'ibama:judge.explainerConama' : 'ibama:judge.explainerOffice'
        }
      />

      <AsOfStrip asOf={data.as_of} />

      {p.track !== 'conama' ? (
        <section className="rounded-lg border bg-card p-4" data-terms data-panel="ibama-terms">
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('ibama:judge.terms.title')}
          </h2>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('ibama:judge.terms.office')}</th>
                  <th className="px-3 py-2 font-medium">{t('ibama:judge.terms.uf')}</th>
                  <th className="px-3 py-2 font-medium">{t('ibama:judge.terms.window')}</th>
                  <th className="px-3 py-2 font-medium">{t('ibama:judge.terms.act')}</th>
                  <th className="px-3 py-2 font-medium">{t('ibama:judge.terms.proof')}</th>
                </tr>
              </thead>
              <tbody>
                {p.terms.map((w, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-2 text-foreground">{officeKindLabel(tf, w.office_kind)}</td>
                    <td className="px-3 py-2 text-muted-foreground">{w.uf ?? '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {w.term_start ?? t('ibama:judge.terms.current')}
                      {' – '}
                      {w.term_end ?? t('ibama:judge.terms.open')}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{actKindLabel(tf, w.act_kind)}</td>
                    <td className="px-3 py-2 text-xs">
                      <a
                        href={w.proof_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {t('ibama:judge.terms.proofLink')}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground" data-attributed-summary>
            {t('ibama:judge.attributed.summary', {
              first: number.format(p.attributed_first),
              board: number.format(p.attributed_board),
              corpus: number.format(p.corpus_judged_total),
            })}
          </p>
          {p.team_attributed > 0 ? (
            <p className="mt-1 text-xs text-muted-foreground" data-team-note>
              {t('ibama:judge.attributed.team', { n: number.format(p.team_attributed) })}
            </p>
          ) : null}
          {p.unresolved_tallied > 0 ? (
            <p className="mt-1 text-xs text-muted-foreground" data-unresolved>
              {t('ibama:judge.attributed.unresolved', { n: number.format(p.unresolved_tallied) })}
            </p>
          ) : null}
          {p.by_year.length > 0 ? (
            <p className="mt-2 text-xs text-muted-foreground" data-by-year>
              {p.by_year.map((y) => `${y.year}: ${number.format(y.attributed)}`).join(' · ')}
            </p>
          ) : null}
        </section>
      ) : null}

      {p.track !== 'conama' && data.attributed_cases.length > 0 ? (
        <section className="rounded-lg border bg-card p-4" data-attributed-cases data-panel="ibama-attributed">
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('ibama:judge.cases.title')}
          </h2>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('ibama:judge.cases.auto')}</th>
                  <th className="px-3 py-2 font-medium">{t('ibama:judge.cases.uf')}</th>
                  <th className="px-3 py-2 font-medium">{t('ibama:judge.cases.instance')}</th>
                  <th className="px-3 py-2 font-medium">{t('ibama:judge.cases.date')}</th>
                  <th className="px-3 py-2 font-medium">{t('ibama:judge.cases.basis')}</th>
                </tr>
              </thead>
              <tbody>
                {data.attributed_cases.map((c, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-2 text-foreground">{c.auto_key}</td>
                    <td className="px-3 py-2 text-muted-foreground">{c.uf}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {t(`ibama:judge.instance.${c.instance}`)}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{c.decision_date ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {t(`ibama:judge.basis.${c.attribution_basis}`)}
                      {c.attribution_basis === 'ENJ_TEAM' ? (
                        <span className="ml-1 rounded bg-muted px-1 py-0.5 text-[10px] uppercase">
                          {t('ibama:judges.bench.teamBadge')}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {p.track === 'conama' ? (
        <section className="rounded-lg border bg-card p-4" data-conama data-panel="ibama-conama">
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('ibama:judge.conama.title')}
          </h2>
          <p className="mb-3 text-xs text-muted-foreground">
            {t('ibama:judge.conama.summary', {
              items: number.format(p.conama_items),
              judged: number.format(p.conama_judged),
              unanimity: number.format(p.conama_unanimity),
            })}
          </p>
          <div className="rounded-md border p-3">
            <div className="mb-1 text-xs font-medium text-foreground">
              {t('ibama:judge.conama.mixTitle')}
            </div>
            <Answer answer={data.disposition_mix} scope="record">
              {(rows) => (
                <p className="text-xs text-muted-foreground" data-dispositions>
                  {rows.map((d) => `${dispositionLabel(tf, d.label)}: ${number.format(d.units)}`).join(' · ')}
                </p>
              )}
            </Answer>
          </div>
          {data.conama_items.length > 0 ? (
            <div className="mt-3 overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('ibama:judge.conama.process')}</th>
                    <th className="px-3 py-2 font-medium">{t('ibama:judge.conama.reunion')}</th>
                    <th className="px-3 py-2 font-medium">{t('ibama:judge.conama.when')}</th>
                    <th className="px-3 py-2 font-medium">{t('ibama:judge.conama.disposition')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.conama_items.map((c, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-2 text-foreground">
                        {c.process_url ? (
                          <a
                            href={c.process_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary underline-offset-2 hover:underline"
                          >
                            {formatNup(c.process_number)}
                          </a>
                        ) : (
                          formatNup(c.process_number)
                        )}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{c.reunion}a</td>
                      <td className="px-3 py-2 text-muted-foreground">{c.when ?? '—'}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {c.disposition ? dispositionLabel(tf, c.disposition) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}

      <p className="text-xs text-muted-foreground">
        <Link to="/ibama/judges" className="text-primary underline-offset-2 hover:underline">
          {t('ibama:judge.backToBench')}
        </Link>
      </p>
    </>
  );
}
